import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { LoopApiError, type SessionUser } from '@innerloopco/loopcms-core'
import { api } from './api.js'

/**
 * `awaiting-totp` is the second step after a password (an authenticator code,
 * a passkey or a recovery code — `methods` says which the person has);
 * `setup-required` is someone with no second way in yet, who must add one.
 */
export type AuthStatus = 'loading' | 'signed-out' | 'awaiting-totp' | 'setup-required' | 'signed-in'
export interface SecondStepMethods { totp: boolean; passkey: boolean }

interface AuthValue {
  user: SessionUser | null
  status: AuthStatus
  methods: SecondStepMethods | null
  signIn: (instance: string, email: string, password: string) => Promise<void>
  verifyTotp: (code: string) => Promise<void>
  /** Sign in with a passkey — on its own when signed out, or as the second step. */
  /** `secondStep` after a password; without it, a passkey signs in on its own. */
  signInWithPasskey: (secondStep?: boolean) => Promise<void>
  /** After the set-up screen added a method: re-read the session, now full. */
  finishSetup: () => Promise<void>
  signOut: () => Promise<void>
  restart: () => void
  /** Take an already-established session (accept-invite hands one back) without another round trip. */
  adopt: (user: SessionUser) => void
  /** Re-read `/api/auth/me` and replace the user — after the Account page saves a name or theme. */
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [methods, setMethods] = useState<SecondStepMethods | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .me()
      .then((found) => {
        if (cancelled) return
        setUser(found)
        setStatus(found.setupRequired ? 'setup-required' : 'signed-in')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // A 401 here is the normal not-signed-in case, not a failure.
        if (!(error instanceof LoopApiError) || error.status !== 401) console.error(error)
        setStatus('signed-out')
      })
    return () => { cancelled = true }
  }, [])

  const signIn = useCallback(async (instance: string, email: string, password: string) => {
    const result = await api.signIn(instance, email, password)
    // A password alone never completes: the second step, or setting one up.
    if (result.step === 'second-step') {
      setMethods(result.methods ?? { totp: true, passkey: false })
      setStatus('awaiting-totp')
      return
    }
    if (result.step === 'setup') {
      setUser(await api.me())
      setStatus('setup-required')
      return
    }
    setUser(result.user ?? null)
    setStatus('signed-in')
  }, [])

  const signInWithPasskey = useCallback(async (secondStep = false) => {
    const optionsJSON = await api.passkeySignInOptions(secondStep)
    const response = await startAuthentication({ optionsJSON: optionsJSON as never })
    const result = await api.passkeySignIn(response)
    setUser(result.user ?? null)
    setMethods(null)
    setStatus('signed-in')
  }, [])

  const finishSetup = useCallback(async () => {
    const found = await api.me()
    setUser(found)
    setStatus(found.setupRequired ? 'setup-required' : 'signed-in')
  }, [])

  const verifyTotp = useCallback(async (code: string) => {
    const result = await api.verifyTotp(code)
    setUser(result.user ?? null)
    setStatus('signed-in')
  }, [])

  const signOut = useCallback(async () => {
    await api.signOut()
    setUser(null)
    setStatus('signed-out')
  }, [])

  const restart = useCallback(() => {
    // Drop the half-finished sign-in too, so it can't hang about in this browser.
    void api.signOut().catch(() => undefined)
    setUser(null)
    setMethods(null)
    setStatus('signed-out')
  }, [])

  const adopt = useCallback((found: SessionUser) => {
    setUser(found)
    // Someone who has just accepted an invite has only a password: their
    // session is for setting up a passkey or authenticator first.
    void api.me().then((me) => setStatus(me.setupRequired ? 'setup-required' : 'signed-in')).catch(() => setStatus('signed-out'))
  }, [])

  const refresh = useCallback(async () => {
    setUser(await api.me())
  }, [])

  const value = useMemo(
    () => ({ user, status, methods, signIn, verifyTotp, signInWithPasskey, finishSetup, signOut, restart, adopt, refresh }),
    [user, status, methods, signIn, verifyTotp, signInWithPasskey, finishSetup, signOut, restart, adopt, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside an AuthProvider')
  return value
}

/** The signed-in user, or null — safe to call outside an AuthProvider (tests, the sign-in screen). */
export function useCurrentUser(): SessionUser | null {
  return useContext(AuthContext)?.user ?? null
}
